import React, { useCallback, useEffect, useRef } from "react";
import { Button, Modal, Spin } from "antd";

import Icons from "components/layout/icons";
import Localize from "components/service/localize";
import {
    ATTENDANCE_ACTIONS,
    ATTENDANCE_SCAN_STATES,
    useAttendanceScan
} from "components/attendance/hooks";


const NOOP = () => {};
const DEFAULT_AUDIO_FEEDBACK = {
    unlockAudio: NOOP,
    playFeedback: NOOP,
    stopFeedback: NOOP
};

const INVALID_QR_REASONS = [
    "invalid_qr",
    "invalid_qr_code",
    "qr_invalid"
];

const DEVICE_CONFLICT_REASONS = [
    "device_already_checked_in",
    "same_phone",
    "same_phone_twice",
    "same_device",
    "another_account",
    "another_worker",
    "device_conflict"
];

const getScannerHintKey = (action) => (
    action === ATTENDANCE_ACTIONS.CHECK_OUT ?
        "ATTENDANCE.Scanner_CheckOut_Hint" :
        "ATTENDANCE.Scanner_CheckIn_Hint"
);

const getSuccessTextKey = (action) => (
    action === ATTENDANCE_ACTIONS.CHECK_OUT ?
        "ATTENDANCE.Result_CheckedOutAt" :
        "ATTENDANCE.Result_CheckInSuccessAt"
);

const formatTime = (value, moment) => {
    if (!value || !moment) {
        return "-";
    }

    const parsedTime = moment(value);

    return parsedTime.isValid() ? parsedTime.format("HH:mm") : "-";
};

const BackButton = ({ onClick }) => (
    <Button type="text" className="attendance-back-button" onClick={ onClick }>
        <Icons.Arrow />
        <Localize>ATTENDANCE.Button_Back</Localize>
    </Button>
);

const ScannerView = ({
        readerId,
        cameraId,
        action,
        detectedQrBounds,
        processing = false,
        processingKey,
        onBack
    }) => {
    const detectedFrameStyle = detectedQrBounds ? {
        top: `${ detectedQrBounds.top }px`,
        left: `${ detectedQrBounds.left }px`,
        width: `${ detectedQrBounds.width }px`,
        height: `${ detectedQrBounds.height }px`
    } : undefined;

    return (
        <div className="attendance-scanner">
            <video
                key={ readerId }
                id={ readerId }
                className="attendance-scanner-reader"
                autoPlay
                muted
                playsInline
                aria-hidden="true"
            />
            <div id={ cameraId } className="attendance-scanner-camera">
                <div className="attendance-scanner-matte" aria-hidden="true" />
                <div
                    className={ `attendance-scanner-frame${
                        detectedQrBounds ? " captured" : ""
                    }` }
                    style={ detectedFrameStyle }
                    aria-hidden="true"
                >
                    <span className="corner top-left" />
                    <span className="corner top-right" />
                    <span className="corner bottom-left" />
                    <span className="corner bottom-right" />
                </div>
            </div>
            <div className="attendance-scanner-controls">
                <p className="attendance-scanner-hint">
                    <Localize>{ getScannerHintKey(action) }</Localize>
                </p>
                <BackButton onClick={ onBack } />
            </div>
            {processing && (
                <div className="attendance-processing">
                    <Spin size="large" />
                    {processingKey && (
                        <span>
                            <Localize>{ processingKey }</Localize>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const StateIcon = ({ tone, children }) => (
    <div className={ `attendance-state-icon ${ tone }` }>
        { children }
    </div>
);

const LocalFailureView = ({ failure, onRetry, onBack }) => {
    const isCamera = failure?.kind === "camera";
    const isLocation = failure?.kind === "location";
    const isLocationTimeout = failure?.reason === "gps_timeout";
    const isSecureContextFailure = failure?.reason === "insecure_context";
    const showRetry = (isCamera || isLocation) && !isSecureContextFailure;

    return (
        <div className="attendance-state attendance-state-local-error">
            <div className="attendance-state-content">
                <StateIcon tone="warning">
                    {isCamera ? <Icons.CameraOff /> : <Icons.LocationOff />}
                </StateIcon>
                <h2>
                    <Localize>
                        {isCamera && !isSecureContextFailure ?
                            "ATTENDANCE.Error_CameraDesign_Title" :
                            isLocation && !isLocationTimeout && !isSecureContextFailure ?
                                "ATTENDANCE.Error_LocationDesign_Title" :
                                failure?.title}
                    </Localize>
                </h2>
                <p>
                    <Localize>
                        {isCamera && !isSecureContextFailure ?
                            "ATTENDANCE.Error_CameraDesign_Text" :
                            isLocation && !isLocationTimeout && !isSecureContextFailure ?
                                "ATTENDANCE.Error_LocationDesign_Text" :
                                failure?.text}
                    </Localize>
                </p>
            </div>
            {showRetry && (
                <div className="attendance-state-action">
                    <Button type="primary" onClick={ onRetry }>
                        <Localize>ATTENDANCE.Button_TryAgain</Localize>
                    </Button>
                    {isCamera && (
                        <small>
                            <Localize>ATTENDANCE.Error_Camera_Settings_Hint</Localize>
                        </small>
                    )}
                </div>
            )}
            <BackButton onClick={ onBack } />
        </div>
    );
};

const getBackendResultCopy = (result, action) => {
    if (result?.titleKey || result?.textKey) {
        return result;
    }

    if (result?.reason === "outside_radius") {
        return {
            titleKey: "ATTENDANCE.Result_OutsideRadius_Title",
            textKey: "ATTENDANCE.Result_OutsideRadius_DesignText"
        };
    }

    if (result?.reason === "outside_schedule") {
        const isCheckOut = result?.action === ATTENDANCE_ACTIONS.CHECK_OUT;

        return {
            titleKey: isCheckOut ?
                "ATTENDANCE.Result_OutsideSchedule_CheckOut_Title" :
                "ATTENDANCE.Result_OutsideSchedule_CheckIn_Title",
            textKey: isCheckOut ?
                "ATTENDANCE.Result_OutsideSchedule_Text" :
                "ATTENDANCE.Result_OutsideSchedule_CheckIn_Text"
        };
    }

    if (INVALID_QR_REASONS.includes(result?.reason)) {
        const resultAction = result?.action || action;
        const isCheckOut = resultAction === ATTENDANCE_ACTIONS.CHECK_OUT;

        return {
            titleKey: isCheckOut ?
                "ATTENDANCE.Result_InvalidQr_CheckOut_Title" :
                "ATTENDANCE.Result_InvalidQr_CheckIn_Title",
            textKey: "ATTENDANCE.Result_InvalidQr_DesignText"
        };
    }

    if (DEVICE_CONFLICT_REASONS.includes(result?.reason)) {
        return {
            titleKey: "ATTENDANCE.Result_DeviceConflict_Title",
            textKey: "ATTENDANCE.Result_DeviceConflict_Text"
        };
    }

    return {
        titleKey: result?.label ? null : "ATTENDANCE.Result_Rejected_Title",
        textKey: result?.detail || result?.message ?
            null :
            "ATTENDANCE.Result_GenericRejected_Text",
        title: result?.label,
        text: result?.detail || result?.message
    };
};

const BackendResultView = ({ result, action, onBack }) => {
    const copy = getBackendResultCopy(result, action);

    return (
        <div className="attendance-state attendance-state-backend-error">
            <div className="attendance-state-content">
                <StateIcon tone="error">
                    <Icons.AttendanceAlert />
                </StateIcon>
                <h2>
                    {copy.titleKey ?
                        <Localize>{ copy.titleKey }</Localize> :
                        copy.title}
                </h2>
                <p>
                    {copy.textKey ?
                        <Localize>{ copy.textKey }</Localize> :
                        copy.text}
                </p>
            </div>
            <BackButton onClick={ onBack } />
        </div>
    );
};

const SuccessView = ({ action, serverTime, moment, onConfirm }) => (
    <div className="attendance-state attendance-state-success">
        <div className="attendance-state-content">
            <StateIcon tone="success">
                <Icons.AttendanceSuccess />
            </StateIcon>
            <h2>
                <Localize
                    time={ formatTime(serverTime, moment) }
                    br={ <br /> }
                >
                    { getSuccessTextKey(action) }
                </Localize>
            </h2>
        </div>
        <div className="attendance-state-action">
            <Button type="primary" onClick={ onConfirm }>
                <Localize>ATTENDANCE.Button_GotIt</Localize>
            </Button>
        </div>
    </div>
);

const AttendanceScannerModal = ({
        visible = false,
        action = ATTENDANCE_ACTIONS.CHECK_IN,
        eeId,
        moment,
        audioFeedback = DEFAULT_AUDIO_FEEDBACK,
        onSuccess = () => {},
        onClose = () => {}
    }) => {
    const successHandledRef = useRef(false);
    const feedbackHandledRef = useRef(false);
    const {
        readerId,
        cameraId,
        scanState,
        result,
        failure,
        detectedQrBounds,
        startScanner,
        stopScanner,
        retry,
        reset
    } = useAttendanceScan({
        action,
        eeId
    });

    const closeScanner = useCallback(() => {
        audioFeedback.stopFeedback();
        stopScanner();
        reset();
        onClose();
    }, [
        audioFeedback,
        onClose,
        reset,
        stopScanner
    ]);

    const retryScanner = useCallback(() => {
        audioFeedback.unlockAudio();
        feedbackHandledRef.current = false;
        retry();
    }, [
        audioFeedback,
        retry
    ]);

    useEffect(() => {
        if (!visible) {
            successHandledRef.current = false;
            stopScanner();
            reset();

            return undefined;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            startScanner();
        });

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            stopScanner();
        };
    }, [
        action,
        eeId,
        reset,
        startScanner,
        stopScanner,
        visible
    ]);

    useEffect(() => {
        if (
            !visible ||
            scanState !== ATTENDANCE_SCAN_STATES.RESULT ||
            result?.result !== "accepted"
        ) {
            return undefined;
        }

        if (!successHandledRef.current) {
            successHandledRef.current = true;
            onSuccess(result);
        }

        return undefined;
    }, [
        onSuccess,
        result,
        scanState,
        visible
    ]);

    useEffect(() => {
        const isResult = scanState === ATTENDANCE_SCAN_STATES.RESULT;
        const isFailed = scanState === ATTENDANCE_SCAN_STATES.FAILED;

        if (!visible || (!isResult && !isFailed)) {
            feedbackHandledRef.current = false;

            return undefined;
        }

        if (!feedbackHandledRef.current) {
            feedbackHandledRef.current = true;
            audioFeedback.playFeedback(
                isResult && result?.result === "accepted" ? "success" : "error"
            );
        }

        return undefined;
    }, [
        audioFeedback,
        result,
        scanState,
        visible
    ]);

    const renderContent = () => {
        if (scanState === ATTENDANCE_SCAN_STATES.RESULT) {
            if (result?.result === "accepted") {
                return (
                    <SuccessView
                        action={ action }
                        serverTime={ result?.server_time }
                        moment={ moment }
                        onConfirm={ closeScanner }
                    />
                );
            }

            return (
                <BackendResultView
                    result={ result }
                    action={ action }
                    onBack={ closeScanner }
                />
            );
        }

        if (scanState === ATTENDANCE_SCAN_STATES.FAILED) {
            if (failure?.kind === "camera" || failure?.kind === "location") {
                return (
                    <LocalFailureView
                        failure={ failure }
                        onRetry={ retryScanner }
                        onBack={ closeScanner }
                    />
                );
            }

            return (
                <BackendResultView
                    result={ {
                        titleKey: failure?.title || "ATTENDANCE.Error_Server_Title",
                        textKey: failure?.text || "ATTENDANCE.Error_Server_Text"
                    } }
                    action={ action }
                    onBack={ closeScanner }
                />
            );
        }

        const isLocating = scanState === ATTENDANCE_SCAN_STATES.LOCATING;
        const isSubmitting = scanState === ATTENDANCE_SCAN_STATES.SUBMITTING;

        return (
            <ScannerView
                readerId={ readerId }
                cameraId={ cameraId }
                action={ action }
                detectedQrBounds={ detectedQrBounds }
                processing={
                    scanState === ATTENDANCE_SCAN_STATES.IDLE ||
                    isLocating ||
                    isSubmitting
                }
                processingKey={
                    isLocating ?
                        "ATTENDANCE.Processing_Locating_Title" :
                        isSubmitting ?
                            "ATTENDANCE.Processing_Submitting_Title" :
                            null
                }
                onBack={ closeScanner }
            />
        );
    };

    return (
        <Modal
            className="modal-standard attendance-scanner-modal full dark"
            visible={ visible }
            footer={ null }
            title={ null }
            closable={ false }
            keyboard
            maskClosable={ false }
            destroyOnClose
            centered={ false }
            onCancel={ closeScanner }
        >
            { renderContent() }
        </Modal>
    );
};

export default AttendanceScannerModal;
